// Syracuse University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - Syracuse's Common Data Set 2025-2026 (CDS_2025-26_Complete.pdf from
//    effectiveness.syr.edu, the Institutional Effectiveness key data page):
//    C1 applicants/admits/enrollees for Fall 2025 with the residency table;
//    C8 test policy for Fall 2027; C21 early decision counts; G1/G5
//    2026-2027 costs. Quirk: the C21 template line still reads "For the Fall
//    2024 entering class", but the file carries a correction note on it
//    ("2025, per Peterson's"), so the early decision counts are Fall 2025.
//  - syracuse.edu admissions pages: dates and deadlines (the table with
//    Early Action), enrollment options (early action is new for fall 2027
//    applicants and non-binding: shown under currentChanges), early
//    decision, first-year admission requirements, and college-specific
//    requirements and test policy. The first-year checklist page still
//    carries an older test line (through Spring 2027); the requirements pages
//    give the current one (through Spring 2028). The deadlines table gives
//    ED II notification as mid-January while the ED pages say early or
//    mid-February; the page shows the table.
//  - College Scorecard, school id 196413 (net price by family income). The
//    API's DEMO_KEY was rate-limited, so the figures were read from the
//    Scorecard's own school page; the five income bands match the 2023-2024
//    column of the Department of Education's College Navigator.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Syracuse Common Data Set 2025-2026',
        url: 'https://effectiveness.syr.edu/wp-content/uploads/2026/09/CDS_2025-26_Complete.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Syracuse Admissions, Dates and Deadlines',
        url: 'https://www.syracuse.edu/admissions-aid/application-process/apply/dates-deadlines/',
        read: '2026-10-01',
    },
    plans: {
        label: 'Syracuse Admissions, Enrollment Options and Admission Plans',
        url: 'https://www.syracuse.edu/admissions-aid/application-process/undergraduate/enrollment-options/',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Syracuse Admissions, Early Decision',
        url: 'https://www.syracuse.edu/admissions-aid/application-process/undergraduate/enrollment-options/early-decision/',
        read: '2026-10-01',
    },
    tests: {
        label: 'Syracuse Admissions, First-Year Admission Requirements',
        url: 'https://www.syracuse.edu/admissions-aid/application-process/undergraduate/first-year/requirements/',
        read: '2026-10-01',
    },
    programs: {
        label: 'Syracuse Admissions, College-Specific Requirements and Standardized Test Policy',
        url: 'https://www.syracuse.edu/admissions-aid/application-process/undergraduate/college-specific-requirements/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?196413-Syracuse-University',
        read: '2026-10-15',
    },
    costs: {
        label: 'Syracuse University, Undergraduate Costs (2026-27 Cost of Attendance)',
        url: 'https://www.syracuse.edu/admissions-aid/cost/undergraduate-costs/',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'syracuse-university',
    kbId: 'syracuse_university',
    name: 'Syracuse University',
    shortName: 'Syracuse',
    aliases: ['Cuse'],
    location: 'Syracuse, NY',
    control: 'Private',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (Syracuse brand colors); decoration only.
    branding: {
        primary: '#F76900',
        secondary: '#000E54',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/syracuse_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 46645,
        admitted: 22756,
        enrolled: 3969,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 13154, admitted: 6028 },
            { group: 'Out-of-state', applied: 29307, admitted: 15356 },
            { group: 'International', applied: 4184, admitted: 1372 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Syracuse\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major. You apply to one of its undergraduate colleges.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set gives one early decision count for the fall 2025 entering class. It does not report early action counts.',
        rows: [
            { plan: 'Early Decision', applied: 2654, admitted: 1478 },
        ],
        source: 'cds',
        where: 'section C21',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of Syracuse\'s undergraduate colleges. Some ask for more than the application:',
        rows: [
            { school: 'School of Architecture', requirement: 'A portfolio, sent through your applicant portal.' },
            { school: 'Visual and Performing Arts, School of Art and Film and Media Arts', requirement: 'A portfolio for every program, except the BS in Studio Arts.' },
            { school: 'Visual and Performing Arts, School of Design', requirement: 'A portfolio for the BFA and BID programs.' },
            { school: 'Visual and Performing Arts, Drama', requirement: 'An audition or portfolio. Acting and musical theater need a prescreen audition: the priority deadline is November 1 and the final deadline December 1, and early decision and early action are not open to them.' },
            { school: 'Visual and Performing Arts, Setnor School of Music', requirement: 'An audition for Music Education. For the Music BS and Music Industry, an audition is optional but encouraged.' },
            { school: 'Engineering and Computer Science', requirement: 'One academic recommendation from a STEM teacher. Four years of college preparatory math (including precalculus or calculus) and laboratory science are recommended, physics strongly.' },
        ],
        note: 'Once you apply, you upload portfolios and auditions through your applicant portal.',
        source: 'programs',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Late December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Early February', binding: false },
            { plan: 'Early Decision II', deadline: 'January 5', decision: 'Mid-January', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'Mid-March', binding: false },
        ],
        note: 'Decision dates are when notifications begin, by email. Architecture, art, design, film and music portfolios and auditions follow the same deadlines.',
        source: 'deadlines',
        binding: 'Early decision I and II are binding only if you are admitted to your first-choice college or program: you agree to enroll and withdraw your other applications. If you are admitted to your second choice or an alternate program, you have until May 1 to reply. If you apply for aid and the award does not make attendance possible, you may decline and be released from the commitment.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Syracuse does not require the SAT or ACT for students applying for fall 2027 admission. If you choose not to send scores, you are not at a disadvantage and you stay eligible for merit scholarships.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 5.',
        cdsWhere: 'section C8',
    },

    // Changes already in effect for this cycle: they apply to students
    // applying in fall 2026 for fall 2027 entry.
    currentChanges: [
        { topic: 'Early action', line: 'Early action is new for fall 2027 applicants. It is not binding: apply by November 1, hear in early February, and you can decide right away or wait until May 1.', source: 'plans' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 69180 },
            { item: 'Housing and food (average, on campus)', amount: 20580 },
            { item: 'Miscellaneous fees', amount: 1869 },
            { item: 'Books, course materials, supplies and equipment', amount: 1847 },
            { item: 'Transportation', amount: 839 },
            { item: 'Personal expenses', amount: 1294 },
            { item: 'Loan fees (if you borrow)', amount: 67 },
        ],
        total: 95676,
        note: 'Undergraduates living on campus. Health insurance ($2,868) is a mandatory fee that can be waived with your own coverage; with it, the total is $98,544.',
        source: 'costs',
        netPrice: {
            average: 43336,
            byIncome: [
                { income: '$0 to $30,000', amount: 21872 },
                { income: '$30,001 to $48,000', amount: 21339 },
                { income: '$48,001 to $75,000', amount: 25499 },
                { income: '$75,001 to $110,000', amount: 33983 },
                { income: 'Over $110,000', amount: 60127 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Syracuse\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
